import {
  computed,
  ref,
  readonly,
  watch,
  ComputedRef,
} from '@nuxtjs/composition-api';
import capitalize from 'lodash/capitalize';
import PricePlans from '@/services/PricePlans';
import { BillingPeriod } from '@/constants/Billing';

export enum PlanProductKey {
  LITE = 'LITE',
  DEDICATED = 'CORE',
  ELITE = 'ELITE',
  // The SuperVision ladder (SUP-8), sold only on the B-side. Two plans per level:
  // Observation, Supervision, Management. See constants/Experiments for the grouping.
  COMPANY_OBSERVATION = 'COMPANY_OBSERVATION',
  WORKFORCE_OBSERVATION = 'WORKFORCE_OBSERVATION',
  STANDARD_SUPERVISION = 'STANDARD_SUPERVISION',
  PRACTICE_SUPERVISION = 'PRACTICE_SUPERVISION',
  WORKFORCE_PRACTICE_MANAGEMENT = 'WORKFORCE_PRACTICE_MANAGEMENT',
  ORGANIZATIONAL_MATTER_MANAGEMENT = 'ORGANIZATIONAL_MATTER_MANAGEMENT',
}

// ============================================================================
// NEW CONSOLIDATED CONFIGURATION TYPES
// ============================================================================

type PlanConfigBillingOverrides = {
  [tierNumber: number]: BillingPeriod;
};

type PlanConfig = {
  availableBillingCycles: BillingPeriod[];
  defaultBillingCycle: BillingPeriod;
  defaultBillingCycleOverridesByTier?: PlanConfigBillingOverrides;
};

type OfferedPlansConfig = {
  [key in PlanProductKey]: PlanConfig;
};

// ============================================================================
// CONSOLIDATED CONFIGURATION - GLOBAL DEFAULTS
// ============================================================================

const GLOBAL_BILLING_CYCLES_OFFERED = [BillingPeriod.MONTH];
const GLOBAL_BILLING_CYCLE_DEFAULT = BillingPeriod.MONTH;
const DEFAULT_PLAN = PlanProductKey.ELITE;

// ============================================================================
// CONSOLIDATED CONFIGURATION - OFFERED PLANS
// ============================================================================
//
// This configuration consolidates all plan configuration into a single source of truth.
// Each plan type specifies:
//   - availableBillingCycles: Which billing periods users can select
//   - defaultBillingCycle: The default billing period for this plan type
//   - defaultBillingCycleOverridesByTier (optional): Tier-specific default overrides
//
// TO MODIFY PLAN BEHAVIOR:
//   1. Update availableBillingCycles to add/remove billing options
//   2. Update defaultBillingCycle to change the default selection
//   3. Add defaultBillingCycleOverridesByTier entries for tier-specific defaults
//
// EXAMPLE - Make Tier 5-7 DEDICATED default to annual:
//   [PlanProductKey.DEDICATED]: {
//     ...
//     defaultBillingCycleOverridesByTier: {
//       5: BillingPeriod.YEAR,
//       6: BillingPeriod.YEAR,
//       7: BillingPeriod.YEAR
//     }
//   }
// ============================================================================

const OFFERED_PLANS_CONFIG: OfferedPlansConfig = {
  [PlanProductKey.LITE]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
  [PlanProductKey.DEDICATED]: {
    availableBillingCycles: [...GLOBAL_BILLING_CYCLES_OFFERED],
    defaultBillingCycle: GLOBAL_BILLING_CYCLE_DEFAULT,
  },
  [PlanProductKey.ELITE]: {
    availableBillingCycles: [...GLOBAL_BILLING_CYCLES_OFFERED],
    defaultBillingCycle: GLOBAL_BILLING_CYCLE_DEFAULT,
  },

  // SuperVision ladder. Monthly only — SUP-8 priced one interval per family, deliberately,
  // because the pricing matrix gives a single amount per plan and tier. Adding intervals here
  // would mean inventing prices.
  [PlanProductKey.COMPANY_OBSERVATION]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
  [PlanProductKey.WORKFORCE_OBSERVATION]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
  [PlanProductKey.STANDARD_SUPERVISION]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
  [PlanProductKey.PRACTICE_SUPERVISION]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
  [PlanProductKey.WORKFORCE_PRACTICE_MANAGEMENT]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
  [PlanProductKey.ORGANIZATIONAL_MATTER_MANAGEMENT]: {
    availableBillingCycles: [BillingPeriod.MONTH],
    defaultBillingCycle: BillingPeriod.MONTH,
  },
};

// ============================================================================
// CONFIGURATION VALIDATION
// ============================================================================

/**
 * Validates OFFERED_PLANS_CONFIG at runtime.
 * Throws errors for invalid configuration.
 *
 * Validations:
 *   1. defaultBillingCycle must be in availableBillingCycles
 *   2. All tier overrides must be in availableBillingCycles
 */
const validateOfferedPlansConfig = (): void => {
  Object.entries(OFFERED_PLANS_CONFIG).forEach(([planKey, config]) => {
    const { availableBillingCycles, defaultBillingCycle, defaultBillingCycleOverridesByTier } = config;

    // Validation 1: Check defaultBillingCycle is in availableBillingCycles
    if (!availableBillingCycles.includes(defaultBillingCycle)) {
      throw new Error(
        `[usePricePlans Config Error] Plan "${planKey}": defaultBillingCycle "${defaultBillingCycle}" is not in availableBillingCycles [${availableBillingCycles.join(', ')}]`
      );
    }

    // Validation 2: Check all tier overrides are in availableBillingCycles
    if (defaultBillingCycleOverridesByTier) {
      Object.entries(defaultBillingCycleOverridesByTier).forEach(([tier, billingCycle]) => {
        if (!availableBillingCycles.includes(billingCycle)) {
          throw new Error(
            `[usePricePlans Config Error] Plan "${planKey}", Tier ${tier}: override billing cycle "${billingCycle}" is not in availableBillingCycles [${availableBillingCycles.join(', ')}]`
          );
        }
      });
    }
  });
};

// Run validation when module loads
validateOfferedPlansConfig();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Resolves the default billing period for a given plan type and tier.
 *
 * Resolution order:
 * 1. Check for tier-specific override in OFFERED_PLANS_CONFIG
 * 2. Check for plan type default in OFFERED_PLANS_CONFIG
 * 3. Fall back to GLOBAL_BILLING_CYCLE_DEFAULT
 *
 * @param productKey - The plan product key (LITE, DEDICATED, ELITE)
 * @param tierNumber - Optional tier number for tier-specific overrides
 * @returns The resolved BillingPeriod
 */
const getDefaultBillingPeriod = (
  productKey?: PlanProductKey,
  tierNumber?: number
): BillingPeriod => {
  // If no productKey, return global default
  if (!productKey) {
    return GLOBAL_BILLING_CYCLE_DEFAULT;
  }

  const planConfig = OFFERED_PLANS_CONFIG[productKey];

  // Step 1: Check for tier-specific override
  if (tierNumber !== undefined && planConfig.defaultBillingCycleOverridesByTier) {
    const tierOverride = planConfig.defaultBillingCycleOverridesByTier[tierNumber];
    if (tierOverride) {
      return tierOverride;
    }
  }

  // Step 2: Return plan type default
  return planConfig.defaultBillingCycle;
};


/**
 * Checks if a specific billing period is available for a plan type.
 * Uses the OFFERED_PLANS_CONFIG configuration.
 *
 * @param productKey - The plan product key
 * @param billingPeriod - The billing period to check
 * @returns boolean indicating if the billing period is available
 */
const isBillingPeriodAvailableForPlanType = (
  productKey: PlanProductKey,
  billingPeriod: BillingPeriod
): boolean => {
  return OFFERED_PLANS_CONFIG[productKey]?.availableBillingCycles.includes(billingPeriod) ?? false;
};

/**
 * Filters plans to only include those with available billing cycles.
 * Ensures exported plan arrays only contain selectable plans.
 *
 * @param plans - Array of plans to filter
 * @returns Filtered array containing only plans with available billing cycles
 */
const filterPlansByAvailableBillingCycles = (plans: IPlan[]): IPlan[] => {
  return plans.filter((plan) =>
    isBillingPeriodAvailableForPlanType(
      plan.productKey as PlanProductKey,
      plan.billingPeriod as BillingPeriod
    )
  );
};

// ============================================================================
// PLAN DATA TYPES
// =============================================================YEAR===============

type PlanDescription = {
  title: string;
  header: string;
  description: string;
  priceCopy: string;
  features: string[];
};

type IPlan = {
  productKey: PlanProductKey;
  partner: string;
  version: string;
  status: string;
  annual: boolean;
  tierNumber: number;
  name: string;
  value: string;
  priceId: string;
  employeeSizeMapping: string;
  minSize: number;
  maxSize: number;
  pause: boolean;
  chatOnly: boolean;
  internal: boolean;
  regulated: boolean;
  price: number;
  monthlyPrice: number;
  setupFee: number;
  setupFeeDiscount: number;
  billingPeriod: string;
  volumeBased: boolean;
  collectTax: boolean;
  tags?: any[];
};

const isLoading = ref(false);
const allPlans = ref([] as IPlan[]);

const preparePlan = (plan) => ({
  employeeSizeName:
    capitalize(plan.employeeSizeMapping?.replace('-', ' - ')) || '',
  ...plan,
});

const fetchPlans = async (params = {}) => {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;

  const plans = await PricePlans.fetchPlans(params);

  allPlans.value = plans
    .map(preparePlan)
    .filter((plan) => plan.employeeSizeMapping !== 'franchise');
  isLoading.value = false;
};

/**
 * COMPUTED
 */

const activePlans = computed(() =>
  allPlans.value.filter(({ status }) => status === 'active')
);

const activeDedicatedPlans = computed(() => {
  const plans = activePlans.value.filter(
    (plan: any) => plan.productKey === PlanProductKey.DEDICATED
  );

  // Filter to only include plans with available billing cycles
  return filterPlansByAvailableBillingCycles(plans);
});
const activeDedicatedMonthlyPlans = computed(() => {
  const plans = activeDedicatedPlans.value.filter(
    (plan: any) => plan.billingPeriod === BillingPeriod.MONTH
  );

  return plans;
});

const activeElitePlans = computed(() => {
  const plans = activePlans.value.filter(
    (plan: any) => plan.productKey === PlanProductKey.ELITE
  );

  // Filter to only include plans with available billing cycles
  return filterPlansByAvailableBillingCycles(plans);
});

const bambeeLitePlans = computed(() => {
  const plans = allPlans.value.filter(
    (plan: any) =>
      plan.productKey === PlanProductKey.LITE &&
      plan.testVariant !== 'abelitev1'
  );

  // Filter to only include plans with available billing cycles
  return filterPlansByAvailableBillingCycles(plans);
});

const activeLitePlans = computed(() =>
  activePlans.value.filter(
    (plan: any) => plan.productKey === PlanProductKey.LITE
  )
);

// Currently set to always be monthly
const bambeeLitePlan = computed(() =>
  activeLitePlans.value.find(
    (plan: any) =>
      plan.billingPeriod === BillingPeriod.MONTH &&
      plan.testVariant !== 'abelitev1'
  )
);

// The SuperVision ladder is priced from the Recurly sellable list, not this Stripe
// list: see modules/SelfServiceRegistration/hooks/useSupervisionPlans.js.

const planTiers = computed(() =>
  activePlans.value
    .filter(({ billingPeriod }) => billingPeriod === BillingPeriod.MONTH)
    // @ts-ignore
    .sort((a, b) => a.maxSize - b.maxSize)
);

const annualPlans = computed(() =>
  activePlans.value
    .filter(({ billingPeriod }) => billingPeriod === BillingPeriod.YEAR)
    // @ts-ignore
    .sort((a, b) => a.maxSize - b.maxSize)
);

const monthlyPlans = computed(() =>
  activePlans.value
    .filter(({ billingPeriod }) => billingPeriod === BillingPeriod.MONTH)
    // @ts-ignore
    .sort((a, b) => a.maxSize - b.maxSize)
);

/**
 * Get plans for the default plan product using its default billing cycle.
 * This is used for the pricing page to dynamically show plans based on configuration.
 */
const defaultPlanWithDefaultBillingCycle = computed(() => {
  const defaultPlanConfig = OFFERED_PLANS_CONFIG[DEFAULT_PLAN];
  const defaultBillingCycle = defaultPlanConfig.defaultBillingCycle;

  const plans = activePlans.value.filter(
    (plan: any) =>
      plan.productKey === DEFAULT_PLAN &&
      plan.billingPeriod === defaultBillingCycle
  );

  // @ts-ignore
  return plans.sort((a, b) => a.maxSize - b.maxSize);
});

/**
 * UTILITIES
 */

const getActivePlansByEmployeeCount = (numOfEmployees) =>
  activePlans.value.filter(
    (plan) => plan.employeeSizeMapping === numOfEmployees
  );

const getAnnualPlanByEmployeeRange = (numOfEmployees) =>
  getActivePlansByEmployeeCount(numOfEmployees).find(
    (p) => p?.billingPeriod === BillingPeriod.YEAR
  );

const getAppropriateDedicatedPlanByTier = (tierNumber = 1) => {
  const billingPeriod = getDefaultBillingPeriod(PlanProductKey.DEDICATED, tierNumber);

  // @ts-ignore
  return activeDedicatedPlans.value.find(
    (p) => p?.billingPeriod === billingPeriod && p?.tierNumber === tierNumber
  );
};

/**
 * Gets the appropriate plan for a tier based on the DEFAULT_PLAN configuration.
 * This is the primary function used by employee size mapping.
 *
 * Resolution:
 * 1. Uses DEFAULT_PLAN to determine which plan type to return
 * 2. Calls the appropriate plan type-specific function
 * 3. Returns the plan with configured default billing period
 *
 * @param tierNumber - The tier number
 * @returns The matching plan or undefined
 */
const getAppropriatePlanByTier = (tierNumber: number = 1): IPlan | undefined => {
  switch (DEFAULT_PLAN as PlanProductKey) {
    case PlanProductKey.DEDICATED:
      return getAppropriateDedicatedPlanByTier(tierNumber);
    case PlanProductKey.ELITE:
      return getAppropriateElitePlanByTier(tierNumber);
    case PlanProductKey.LITE:
      // LITE plans don't have tier numbers in the traditional sense
      // Return the bambeeLitePlan
      return bambeeLitePlan.value;
    default:
      return getAppropriateDedicatedPlanByTier(tierNumber);
  }
};

const getAppropriatePlanByEmployeeRange = (numOfEmployees) => {
  const activePlansByCount = getActivePlansByEmployeeCount(numOfEmployees);
  // @ts-ignore
  const activeTier = activePlansByCount[0]?.tierNumber;

  // Use configuration-based plan type instead of hardcoded DEDICATED
  return getAppropriatePlanByTier(activeTier);
};

/**
 * Gets the appropriate plan for any product type and tier based on configured defaults.
 * This is a more flexible version of getAppropriateDedicatedPlanByTier.
 *
 * @param productKey - The plan product key
 * @param tierNumber - The tier number
 * @returns The matching plan or undefined
 */
const getAppropriatePlanByProductAndTier = (
  productKey: PlanProductKey,
  tierNumber: number = 1
): IPlan | undefined => {
  const billingPeriod = getDefaultBillingPeriod(productKey, tierNumber);

  const plansForProduct = activePlans.value.filter(
    (plan) => plan.productKey === productKey
  );

  return plansForProduct.find(
    (p) => p?.billingPeriod === billingPeriod && p?.tierNumber === tierNumber
  );
};

/**
 * Gets the appropriate Elite plan for a given tier based on configured defaults.
 *
 * @param tierNumber - The tier number
 * @returns The matching Elite plan or undefined
 */
const getAppropriateElitePlanByTier = (tierNumber: number = 1): IPlan | undefined => {
  const billingPeriod = getDefaultBillingPeriod(PlanProductKey.ELITE, tierNumber);

  return activeElitePlans.value.find(
    (p) => p?.billingPeriod === billingPeriod && p?.tierNumber === tierNumber
  );
};

const getPlanById = (id = '') => {
  return allPlans.value.find((plan) => plan.value === id);
};

const getEmployeeRangeByTier = (tier: number): string => {
  // @ts-ignore
  return (
    activePlans.value.find((p) => p.tierNumber === tier)?.employeeSizeMapping ||
    ''
  );
};

const calculateSetupFeeWithDiscount = (setupFee, discount) => {
  return setupFee - setupFee * discount;
};

/**
 * STATE
 */

const selectedPlanId = ref('');
const waivedSetupFee = ref(false);
const dollarTrial = ref(true); // TODO: Switch to hasTrial
const selectedPlan = computed(() => getPlanById(selectedPlanId.value));

const selectedSetupFee = computed(() => {
  const setupFee =
    waivedSetupFee.value || dollarTrial.value
      ? 0
      : // @ts-ignore
        selectedPlan.value?.setupFee;

  return calculateSetupFeeWithDiscount(
    setupFee,
    // @ts-ignore
    selectedPlan.value?.setupFeeDiscount
  );
});

const selectedTotalSubscriptionDue = computed(() => {
  // @ts-ignore
  const monthlyPrice = selectedPlan.value?.monthlyPrice || 0;

  return selectedPlan.value?.annual ? monthlyPrice * 12 : monthlyPrice;
});

const selectedTotalPaymentDue = computed(() => {
  return selectedTotalSubscriptionDue.value + selectedSetupFee.value;
});

const setSelectedPlanById = (id) => {
  if (selectedPlanId.value !== id) {
    selectedPlanId.value = id;
  }
};

const removeSelectedPlan = () => {
  selectedPlanId.value = '';
};

const setWaivedSetupFee = (waived = false) => {
  waivedSetupFee.value = waived;
};

const currentProduct = computed(() => selectedPlan.value?.productKey);

const currentTier = computed(() => selectedPlan.value?.tierNumber);

// get all plans whose product key matches the currentProduct.value so the user can select which billing interval they want
const getPlansByProduct = (productKey: PlanProductKey) =>
  activePlans.value.filter(
    (plan) =>
      plan.productKey === productKey && plan.tierNumber === currentTier.value
  );

const availablePlansByProduct: ComputedRef<IPlan[]> = computed(() => {
  // @ts-ignore
  return getPlansByProduct(currentProduct.value);
});

const setSelectedPlanByBillingInterval = (billingPeriod: BillingPeriod) => {
  const plan = selectedPlan.value;
  if (plan && plan.billingPeriod !== billingPeriod) {
    const planByBillingPeriod = availablePlansByProduct.value.find(
      (p) => p.billingPeriod === billingPeriod
    );

    if (planByBillingPeriod) {
      selectedPlanId.value = planByBillingPeriod.value;
    }
  }
};

const isReady = computed(() => {
  return !isLoading.value && !!activePlans.value.length;
});

/**
 * Default billing period for the currently selected plan.
 * Returns the configured default based on the plan's product type and tier.
 */
const defaultBillingPeriodForSelectedPlan = computed(() => {
  if (!selectedPlan.value) {
    return GLOBAL_BILLING_CYCLE_DEFAULT;
  }

  return getDefaultBillingPeriod(
    selectedPlan.value.productKey as PlanProductKey,
    selectedPlan.value.tierNumber
  );
});

/**
 * Boolean indicating if the current plan's default is monthly billing.
 * Useful for UI components that need to conditionally render based on default billing period.
 */
const isMonthlyDefault = computed(() => {
  return defaultBillingPeriodForSelectedPlan.value === BillingPeriod.MONTH;
});

const isT3Plus = ref<boolean | null>(null);

// On initial load, set the isT3Plus
watch(
  selectedPlan,
  (plan) => {
    if (isT3Plus.value === null && plan) {
      isT3Plus.value = (selectedPlan.value?.tierNumber || 1) >= 3;
    }
  },
  {
    immediate: true,
  }
);

const usePricePlans = (
  options = {
    force: false,
    params: {},
  }
) => {
  if (options.force || !allPlans.value.length) {
    fetchPlans(options.params || {});
  }

  return {
    isLoading: readonly(isLoading),
    isReady,
    plans: activePlans,
    dedicatedPlans: activeDedicatedPlans,
    elitePlans: activeElitePlans,
    dedicatedMonthlyPlans: activeDedicatedMonthlyPlans,
    annualPlans,
    tiers: planTiers,
    getAnnualPlanByEmployeeRange,
    getAppropriatePlanByEmployeeRange,
    getActivePlansByEmployeeCount,
    selectedPlanId,
    removeSelectedPlan,
    getAppropriateDedicatedPlanByTier,
    getEmployeeRangeByTier,
    getPlanById,
    selectedPlan,
    selectedSetupFee,
    selectedTotalSubscriptionDue,
    selectedTotalPaymentDue,
    setSelectedPlanById,
    setWaivedSetupFee,
    calculateSetupFeeWithDiscount,
    bambeeLitePlan,
    bambeeLitePlans,
    availablePlansByProduct,
    setSelectedPlanByBillingInterval,
    isT3Plus,
    // Configuration-based billing period management
    getDefaultBillingPeriod,
    getAppropriatePlanByProductAndTier,
    getAppropriateElitePlanByTier,
    getAppropriatePlanByTier,
    defaultBillingPeriodForSelectedPlan,
    isMonthlyDefault,
    isBillingPeriodAvailableForPlanType,
    // Dynamic pricing page support
    defaultPlanWithDefaultBillingCycle,
    DEFAULT_PLAN,
    OFFERED_PLANS_CONFIG,
  };
};

export default usePricePlans;
